import type { BoardSlide } from "./whiteboard";

export type ThemeMode = "light" | "dark";
export type LocaleCode = "vi" | "zh-CN" | "zh-TW" | "en";
export type ExcalidrawLangCode = "vi-VN" | "zh-CN" | "zh-TW" | "en";

export const VALID_LOCALE_CODES: readonly LocaleCode[] = [
  "vi",
  "zh-CN",
  "zh-TW",
  "en",
] as const;

export function isLocaleCode(value: unknown): value is LocaleCode {
  return (
    typeof value === "string" &&
    (VALID_LOCALE_CODES as readonly string[]).includes(value)
  );
}

export function isChineseLocale(locale: LocaleCode): boolean {
  return locale === "zh-CN" || locale === "zh-TW";
}

export function toExcalidrawLangCode(locale: LocaleCode): ExcalidrawLangCode {
  switch (locale) {
    case "vi":
      return "vi-VN";
    case "zh-CN":
      return "zh-CN";
    case "zh-TW":
      return "zh-TW";
    case "en":
      return "en";
  }
}

export type SaveStatus = "saved" | "saving" | "error";
export type BackgroundPreset =
  | "blank"
  | "grid-small"
  | "grid-large"
  | "dots"
  | "lined"
  | "tianzige"
  | "mizige"
  | "pinyin-tianzige";

export const VALID_BACKGROUND_PRESETS: readonly BackgroundPreset[] = [
  "blank",
  "grid-small",
  "grid-large",
  "dots",
  "lined",
  "tianzige",
  "mizige",
  "pinyin-tianzige",
] as const;

export interface SurfaceDimensions {
  readonly width: number;
  readonly height: number;
  readonly aspectRatio?: number;
}

export type ViewportFrameMode = "contain" | "cover" | "fixed_canvas";

export interface PresetSurface {
  readonly kind: "preset";
  readonly preset: BackgroundPreset;
}

export interface DocumentFrameSurface {
  readonly kind: "document_frame";
  readonly documentId: string;
  readonly frameIndex: number;
  readonly assetId: string;
  readonly assetUrl: string;
  readonly dimensions: SurfaceDimensions;
  readonly viewportMode?: ViewportFrameMode;
  readonly mimeType?: string;
}

export interface StaticMediaSurface {
  readonly kind: "static_media";
  readonly assetId: string;
  readonly assetUrl: string;
  readonly dimensions: SurfaceDimensions;
  readonly mimeType?: string;
}

export interface MediaVideoSurface {
  readonly kind: "media_video";
  readonly assetId: string;
  readonly assetUrl: string;
  readonly dimensions: SurfaceDimensions;
  readonly playbackState?: {
    readonly currentTime: number;
    readonly isPlaying: boolean;
    readonly isMuted: boolean;
  };
}

export const SUPPORTED_OFFICE_EXTENSIONS = [".pptx", ".ppt", ".docx", ".doc"] as const;
export const SUPPORTED_DOCUMENT_EXTENSIONS = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ...SUPPORTED_OFFICE_EXTENSIONS,
] as const;

export type SlideSurface =
  | PresetSurface
  | DocumentFrameSurface
  | StaticMediaSurface
  | MediaVideoSurface;

export interface IngestedFrame {
  readonly frameIndex: number;
  readonly assetId: string;
  readonly assetUrl: string;
  readonly dimensions: SurfaceDimensions;
  readonly mimeType: string;
}

export interface DocumentManifest {
  readonly documentId: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly totalFrames: number;
  readonly frames: readonly IngestedFrame[];
  readonly slides: readonly BoardSlide[];
}

export function getSlideBackgroundPreset(surface: SlideSurface): BackgroundPreset {
  return surface.kind === "preset" ? surface.preset : "blank";
}

export function isBackgroundPreset(value: unknown): value is BackgroundPreset {
  return typeof value === "string" && (VALID_BACKGROUND_PRESETS as readonly string[]).includes(value);
}

export interface CanvasBackgroundSwatchPair {
  readonly light: string;
  readonly dark: string;
}

export const CANVAS_BG_SWATCH_PAIRS: readonly CanvasBackgroundSwatchPair[] = [
  { light: "#FFFFFF", dark: "#121212" },
  { light: "#F8F9FA", dark: "#1A1D21" },
  { light: "#F5FAFF", dark: "#121A24" },
  { light: "#FFFCE8", dark: "#1E1B12" },
  { light: "#FDF8F6", dark: "#211519" },
] as const;

export function resolveThemeCanvasBackgroundColor(
  slideBackgroundColor: string,
  theme: ThemeMode
): string {
  const isDark = theme === "dark";
  const defaultPair = CANVAS_BG_SWATCH_PAIRS[0]!;
  const fallbackBg = isDark ? defaultPair.dark : defaultPair.light.toLowerCase();
  const normalizedColor = slideBackgroundColor ? slideBackgroundColor.trim() : "";
  if (!normalizedColor || normalizedColor.toLowerCase() === "transparent") {
    return fallbackBg;
  }

  const upperColor = normalizedColor.toUpperCase();
  const matchedPair = CANVAS_BG_SWATCH_PAIRS.find(
    (pair) => pair.light === upperColor || pair.dark === upperColor
  );
  if (matchedPair) {
    if (matchedPair === defaultPair) {
      return fallbackBg;
    }
    return isDark ? matchedPair.dark : matchedPair.light;
  }

  return normalizedColor;
}

